/**
 * Client-side analytics: batched, fire-and-forget event tracking.
 *
 * Events queue locally and flush to POST /api/analytics/events every 15s (or
 * at 10 queued events); on pagehide the queue goes out via sendBeacon so the
 * session_end survives tab close. Everything is fail-silent — analytics must
 * never break or slow the app — and the whole module self-disables under
 * automation (navigator.webdriver, i.e. Playwright/CI) or with ?noanalytics.
 *
 * Event names must match the server whitelist in src/routes/analytics.js.
 * Privacy: events themselves carry only the self-chosen display name + room
 * name. The batch envelope additionally carries the meeting-session token when
 * the visitor entered through an invite, so the server can attribute the visit
 * to that invite (see sessionTokenForIngest); no personal data is ever sent
 * from here, and none is stored on the events.
 */

import { MobileUtils } from '../utils/mobileUtils.js';
import { detectClientEnv } from '../utils/clientEnv.js';
import { DWELL_CELL_SIZE, cellOf, encodeCells } from './dwellGrid.js';
import { sessionStore } from '../state/sessionStore';

const ENDPOINT = '/api/analytics/events';
const FLUSH_INTERVAL_MS = 15_000;
const FLUSH_AT_QUEUE_SIZE = 10;
const MAX_BATCH_SIZE = 20; // keep in sync with the server cap
const PERF_SAMPLE_WINDOW = 30; // 1s samples per perf_sample event
const DWELL_SAMPLE_MS = 2000; // position sample cadence
const DWELL_EMIT_SAMPLES = 30; // samples per dwell_samples event (60s window)
const PATH_SAMPLE_EVERY = 2; // every 2nd dwell tick -> a path point (4s)
// Hard cap per window. A 60s window yields 15 points; this only bites when
// hidden-tab skips stretch a window, and keeps the payload inside the server's
// 2 KB JSON cap with room to spare.
const PATH_MAX_POINTS = 60;

const disabled =
  (typeof navigator !== 'undefined' && navigator.webdriver) ||
  (typeof window !== 'undefined' && new URLSearchParams(window.location.search).has('noanalytics'));

let sessionId = null;
let startedAt = 0;
let queue = [];
let sessionEnded = false;
let perfTimer = null;
let dwellTimer = null;
// Pending dwell accumulation, flushed by the sampler's own window AND by
// endSession so the tail of a visit isn't lost on tab close.
let dwellCells = new Map();
// Pending ORDERED path samples for the same window - what the dwell cells
// deliberately throw away (encodeCells sorts by dwell time, so visit order is
// gone). Drained by the same emit boundary. See docs/visit-replay.md.
let pathPoints = [];
let pathT0 = 0;
let pathSeq = 0;
// The level both meters are currently accumulating for. A level switch has to
// drain them: the two levels have unrelated coordinate spaces, so merging them
// silently overlays a breakout lounge onto the exhibition hall.
let sampleLevel = null;
// Shared world-session ID for the room this client is currently in. Resolved
// at Photon join (src/multiplayer.js), null before join and between rooms.
let worldSessionId = null;

// Common fields resolved at enqueue time from the authoritative identity.
function makeEvent(name, payload) {
  return {
    name,
    sessionId,
    worldSessionId,
    roomName: sessionStore.getState().roomName || null,
    playerName: sessionStore.getState().playerName || null,
    role: sessionStore.getState().userRole || null,
    engineType: window.ENGINE_TYPE || null,
    payload,
  };
}

// The meeting-session token, when this visitor entered through an invite. The
// server verifies it and reads its inviteId claim to attribute the visit; it is
// never trusted from anywhere else, and open-access visitors (whose token is
// oggi_presence and carries no inviteId) simply don't send one.
//
// It rides in the BODY rather than an Authorization header because the pagehide
// flush uses sendBeacon, which cannot set headers - the same reason
// sendLeaveBeacon in src/multiplayer/participantRegistry.ts posts its token
// this way.
function sessionTokenForIngest() {
  try {
    return localStorage.getItem('oggi_session') || undefined;
  } catch {
    return undefined; // storage can throw in locked-down/private contexts
  }
}

function flush({ useBeacon = false } = {}) {
  if (queue.length === 0) return;
  const batch = queue.slice(0, MAX_BATCH_SIZE);
  queue = queue.slice(batch.length);
  const body = JSON.stringify({ events: batch, sessionToken: sessionTokenForIngest() });

  try {
    if (useBeacon && navigator.sendBeacon) {
      navigator.sendBeacon(ENDPOINT, new Blob([body], { type: 'application/json' }));
      return;
    }
    fetch(ENDPOINT, {
      method: 'POST',
      keepalive: true,
      headers: { 'Content-Type': 'application/json' },
      body,
    }).catch(() => {}); // fire-and-forget; a failed flush drops the batch
  } catch {
    // never let telemetry surface an error
  }
}

// Drain both position meters into their events. Called on the sampler's emit
// window, on a level change, and from endSession (before the final beacon).
//
// `level` is stamped on both payloads: dwell_samples has never carried one, so
// cells from two levels used to merge into one coordinate space. The heatmap
// RPC ignores unknown payload keys, so adding it is backward compatible.
function emitPositionSamples() {
  if (dwellCells.size > 0) {
    const cells = encodeCells(dwellCells);
    dwellCells = new Map();
    queue.push(
      makeEvent('dwell_samples', { cellSize: DWELL_CELL_SIZE, cells, level: sampleLevel }),
    );
  }
  if (pathPoints.length > 0) {
    const points = pathPoints;
    pathPoints = [];
    queue.push(
      makeEvent('path_samples', {
        cellSize: DWELL_CELL_SIZE,
        level: sampleLevel,
        t0: pathT0,
        // Monotone per-visit window counter. Flush has no retry and these
        // points are deltas, not high-water marks, so a dropped batch punches
        // a hole; the gap in seq is what lets the viewer draw it honestly
        // instead of interpolating a straight line through it.
        seq: pathSeq++,
        points,
      }),
    );
  }
  pathT0 = 0;
}

function endSession() {
  if (sessionEnded || !sessionId) return;
  sessionEnded = true;
  emitPositionSamples();
  const durationSecs = Math.round((Date.now() - startedAt) / 1000);
  queue.push(makeEvent('session_end', { durationSecs }));
  flush({ useBeacon: true });
}

export const analytics = {
  /**
   * Start a session after loginFlow commits the resolved identity.
   */
  init() {
    if (disabled || sessionId) return;
    sessionId = crypto.randomUUID();
    startedAt = Date.now();

    const clientEnv = detectClientEnv();

    this.track('session_start', {
      device: MobileUtils.isMobile() ? 'mobile' : 'desktop',
      screenW: clientEnv.screenW,
      screenH: clientEnv.screenH,
      deviceClass: clientEnv.deviceClass,
      os: clientEnv.os,
      browser: clientEnv.browser,
      browserVersion: clientEnv.browserVersion,
      dpr: clientEnv.dpr,
      cores: clientEnv.cores,
      deviceMemory: clientEnv.deviceMemory,
    });
    flush(); // open the session row immediately

    // Runs for the page lifetime — no teardown needed
    setInterval(flush, FLUSH_INTERVAL_MS);

    // pagehide is the reliable end-of-visit signal (fires on close, navigate,
    // and mobile background-kill); visibilitychange->hidden just flushes so
    // long-backgrounded tabs don't sit on unsent events.
    window.addEventListener('pagehide', endSession);
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState !== 'hidden' || sessionEnded) return;
      // Drain the position meters first: a backgrounded tab that gets killed
      // never fires pagehide, so an undrained partial window is lost outright.
      emitPositionSamples();
      flush({ useBeacon: true });
    });
  },

  /**
   * Set (or clear) the shared world-session ID attached to every subsequent
   * event. Called by src/multiplayer.js: with an ID once the room's session is
   * resolved at join, and with null when leaving a room. Safe pre-init and
   * when disabled - it only mutates a module var; track() still gates output.
   * @param {string|null} id
   */
  setWorldSession(id) {
    worldSessionId = id || null;
  },

  /** @returns {string|null} the current world-session ID (for adoption dedupe). */
  getWorldSession() {
    return worldSessionId;
  },

  /**
   * Queue an event. Safe to call from anywhere, never throws, never awaited.
   * @param {string} name - Must be in the server whitelist (src/routes/analytics.js)
   * @param {Object} [payload]
   */
  track(name, payload = {}) {
    if (disabled || !sessionId || sessionEnded) return;
    try {
      queue.push(makeEvent(name, payload));
      if (queue.length >= FLUSH_AT_QUEUE_SIZE) flush();
    } catch {
      // swallow — telemetry must not break the app
    }
  },

  /**
   * Sample FPS once per second; emit one perf_sample event per 30s window.
   * @param {() => number} getFps - e.g. () => engine.getFps()
   * @param {() => (number|null)} [getTargetFps] - the active frame cap
   *   (engine.maxFPS), if any; stamped on the sample so dashboards don't read
   *   a capped tier's steady ~30fps as a performance regression
   */
  startPerfSampling(getFps, getTargetFps) {
    if (disabled || perfTimer) return;
    let samples = [];
    perfTimer = setInterval(() => {
      try {
        const fps = getFps();
        if (Number.isFinite(fps) && fps > 0) samples.push(fps);
        if (samples.length >= PERF_SAMPLE_WINDOW) {
          const avg = samples.reduce((a, b) => a + b, 0) / samples.length;
          const targetFps = getTargetFps?.();
          this.track('perf_sample', {
            avgFps: Math.round(avg * 10) / 10,
            minFps: Math.round(Math.min(...samples) * 10) / 10,
            ...(Number.isFinite(targetFps) ? { targetFps } : {}),
          });
          samples = [];
        }
      } catch {
        // engine may be disposed mid-interval; skip the tick
      }
    }, 1000);
  },

  /**
   * Sample the visitor's ground position every 2s. One timer, two meters:
   *   - every tick credits a quantized dwell cell (the heatmap, unordered)
   *   - every 2nd tick appends an ORDERED path point (the replay)
   * Both drain together on the 60s window boundary, on a level change, and on
   * session end. See docs/dwell-heatmaps.md and docs/visit-replay.md.
   *
   * @param {() => ({x: number, z: number}|null)} getPosition - e.g. the active
   *   camera's position; return null while no camera/level is ready.
   * @param {() => (string|null|undefined)} [getLevel] - the current level id,
   *   stamped on both payloads and drained on change.
   */
  startDwellSampling(getPosition, getLevel) {
    if (disabled || dwellTimer) return;
    let samples = 0;
    dwellTimer = setInterval(() => {
      try {
        // A hidden tab isn't dwelling at an exhibit - don't count it.
        if (document.visibilityState === 'hidden') return;
        const pos = getPosition();
        if (!pos || !Number.isFinite(pos.x) || !Number.isFinite(pos.z)) return;

        // Level change drains first, so the outgoing level's samples are
        // stamped with the level they were actually taken in.
        const level = (typeof getLevel === 'function' ? getLevel() : null) || null;
        if (level !== sampleLevel) {
          emitPositionSamples();
          samples = 0;
          sampleLevel = level;
        }

        const { gx, gz } = cellOf(pos.x, pos.z);
        dwellCells.set(
          `${gx},${gz}`,
          (dwellCells.get(`${gx},${gz}`) || 0) + DWELL_SAMPLE_MS / 1000,
        );

        // Path points are half-rate: 4s is enough to read as a walk, and
        // deliberately too coarse to be a fine-grained behavioural trace.
        if (samples % PATH_SAMPLE_EVERY === 0) {
          const now = Date.now();
          if (pathPoints.length === 0) pathT0 = now;
          if (pathPoints.length < PATH_MAX_POINTS) {
            pathPoints.push([gx, gz, Math.round((now - pathT0) / 1000)]);
          }
        }

        if (++samples >= DWELL_EMIT_SAMPLES) {
          samples = 0;
          emitPositionSamples();
        }
      } catch {
        // camera/scene may be disposed mid-interval; skip the tick
      }
    }, DWELL_SAMPLE_MS);
  },
};
